import { useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import _ from "lodash";
import { ReactElement, useMemo, useState } from "react";

import {
  GetFundraisingAnalyticsDashboardDataQuery,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import {
  COMPUTE_FUNDRAISING_ANALYTICS_DASHBOARD_DATA,
  GET_FUNDRAISING_ANALYTICS_DASHBOARD_DATA,
} from "~frontend/graphql/analyticsDashboardData";
import {
  ANALYTICS_EMBED_FOR_SUBACCOUNT,
  ANALYTICS_EMBED_FOR_TENANT,
} from "~frontend/graphql/analyticsEmbed";
import { Subaccount } from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { formatDateTime } from "~frontend/util/dateFormat";

import { AsyncWrapper } from "../AsyncWrapper";
import ErrorCard from "../ErrorCard";
import { FlexBox } from "../FlexBox";
import Loading from "../Loading";
import { AnalyticsDashboardControls } from "./AnalyticsDashboardControls";
import { AnalyticsDashboardSection } from "./AnalyticsDashboardSection";
import { AnalyticsDataTable } from "./AnalyticsDataTable";
import { AnalyticsEmbed } from "./AnalyticsEmbed";
import { DonorConversionRateChart } from "./DonorConversionRateChart";
import { LinkConversionRateChart } from "./LinkConversionRateChart";
import { MonthlyAverageDonationChart } from "./MonthlyAverageDonationChart";
import { MonthlyDonationsByTypeChart } from "./MonthlyDonationsByTypeChart";
import { MonthlyRoisChart } from "./MonthlyRoisChart";
import { TopLineStat } from "./TopLineStat";
import { fillInMissingMonthlyBuckets, Month } from "./monthlyBuckets";
import { maybeNumberToDollars, maybeNumberToPercentage } from "./numbers";
import { DEFAULT_TIMEFRAME, Timeframe } from "./timeframes";
import { useAnalyticsDashboardData } from "./useAnalyticsDashboardData";

export function FundraisingAnalytics({
  subaccount,
  visible,
}: {
  subaccount?: Subaccount;
  visible: boolean;
}): ReactElement {
  const analyticsDashboardV2Enabled = useFeatureFlag("analyticsDashboardV2");

  if (!visible) {
    return <></>;
  }

  if (analyticsDashboardV2Enabled) {
    return <FundraisingAnalyticsDashboardV2 subaccount={subaccount} />;
  } else {
    return <FundraisingAnalyticsEmbed subaccount={subaccount} />;
  }
}

function FundraisingAnalyticsDashboardV2({
  subaccount,
}: {
  subaccount?: Subaccount;
}): ReactElement {
  const { timeframe, refresh, loading, data, computeInProgress, errors } =
    useAnalyticsDashboardData<GetFundraisingAnalyticsDashboardDataQuery>({
      dataQuery: GET_FUNDRAISING_ANALYTICS_DASHBOARD_DATA,
      dataSelector: data => data.getFundraisingAnalyticsDashboardData,
      computeDataMutation: COMPUTE_FUNDRAISING_ANALYTICS_DASHBOARD_DATA,
      subaccount,
    });

  return (
    <>
      {data?.getFundraisingAnalyticsDashboardData ? (
        <Typography variant="body3">
          Last computed at{" "}
          {formatDateTime(data.getFundraisingAnalyticsDashboardData.computedAt)}
        </Typography>
      ) : (
        <Box sx={{ height: "10px" }} />
      )}
      <FlexBox
        justifyContent="space-between"
        alignItems="flex-end"
        sx={{ marginBottom: "16px" }}
      >
        {data?.getFundraisingAnalyticsDashboardData ? (
          <Typography variant="h1Bold">
            Recent Fundraising Performance
          </Typography>
        ) : (
          <Typography variant="h1Bold" />
        )}
        <AnalyticsDashboardControls
          timeframe={timeframe}
          onSubmit={refresh}
          disabled={loading || computeInProgress}
        />
      </FlexBox>
      <AsyncWrapper data={data} loading={loading} errors={errors}>
        {data => (
          <FundraisingAnalyticsDashboardV2Inner
            data={data}
            onRefresh={() => refresh(timeframe)}
          />
        )}
      </AsyncWrapper>
    </>
  );
}

function FundraisingAnalyticsDashboardV2Inner({
  data,
  onRefresh,
}: {
  data: GetFundraisingAnalyticsDashboardDataQuery;
  onRefresh: () => void;
}): ReactElement {
  const monthlyData = useMemo(() => {
    if (data.getFundraisingAnalyticsDashboardData?.monthlyStats) {
      return fillInMissingMonthlyBuckets(
        data.getFundraisingAnalyticsDashboardData.monthlyStats.map(bucket => ({
          ...bucket,
          month: Month.fromString(bucket.month),
        }))
      );
    }

    return null;
  }, [data]);

  if (
    data.getFundraisingAnalyticsDashboardData?.computeJob?.status ===
    TrackedJobStatus.Failed
  ) {
    return (
      <ErrorCard
        errors={["Compute failed. Please try again."]}
        skipApollo
        onClickReload={onRefresh}
      />
    );
  }

  return (
    <>
      <FlexBox flexDirection="column" alignItems="flex-start" gap="40px">
        <FlexBox
          justifyContent="center"
          flexWrap="wrap"
          gap={1}
          sx={{ width: "100%" }}
        >
          <TopLineStat
            label="ROI"
            value={maybeNumberToPercentage(
              data.getFundraisingAnalyticsDashboardData?.recentPerformance?.roi,
              1
            )}
          />
          <TopLineStat
            label="Average Donation"
            value={maybeNumberToDollars(
              data.getFundraisingAnalyticsDashboardData?.recentPerformance
                ?.averageDonationDollars
            )}
          />
          <TopLineStat
            label="Total Raised"
            value={maybeNumberToDollars(
              data.getFundraisingAnalyticsDashboardData?.recentPerformance
                ?.totalRaisedDollars
            )}
          />
          <TopLineStat
            label="Texts Sent"
            value={
              data.getFundraisingAnalyticsDashboardData?.recentPerformance?.textsSent.toLocaleString() ??
              null
            }
          />
          <TopLineStat
            label="Donations"
            value={
              data.getFundraisingAnalyticsDashboardData?.recentPerformance?.donations.toLocaleString() ??
              null
            }
          />
          <TopLineStat
            label="Total Raised (One-Time)"
            value={maybeNumberToDollars(
              data.getFundraisingAnalyticsDashboardData?.recentPerformance
                ?.totalRaisedOneTimeDollars
            )}
          />
          <TopLineStat
            label="Donor Conversion Rate"
            value={maybeNumberToPercentage(
              data.getFundraisingAnalyticsDashboardData?.recentPerformance
                ?.donorConversionRate,
              2
            )}
          />
          <TopLineStat
            label="Link Conversion"
            value={maybeNumberToPercentage(
              data.getFundraisingAnalyticsDashboardData?.recentPerformance
                ?.linkConversionRate,
              1
            )}
          />
          <TopLineStat
            label="Total Raised (Recurring)"
            value={maybeNumberToDollars(
              data.getFundraisingAnalyticsDashboardData?.recentPerformance
                ?.totalRaisedRecurringDollars
            )}
          />
        </FlexBox>
        <AnalyticsDashboardSection title="Historical Fundraising Performance">
          <FlexBox
            justifyContent="center"
            flexWrap="wrap"
            gap={1}
            sx={{ width: "100%" }}
          >
            <TopLineStat
              label="Lifetime ROI"
              value={maybeNumberToPercentage(
                data.getFundraisingAnalyticsDashboardData?.historicalPerformance
                  ?.roi,
                1
              )}
            />
            <TopLineStat
              label="Lifetime Average Donation"
              value={maybeNumberToDollars(
                data.getFundraisingAnalyticsDashboardData?.historicalPerformance
                  ?.averageDonationDollars
              )}
            />
            <TopLineStat
              label="Lifetime Amount Raised"
              value={maybeNumberToDollars(
                data.getFundraisingAnalyticsDashboardData?.historicalPerformance
                  ?.totalRaisedDollars
              )}
            />
          </FlexBox>
        </AnalyticsDashboardSection>
        <AnalyticsDataTable
          title="Top Performing Campaigns"
          loading={
            !data.getFundraisingAnalyticsDashboardData?.topPerformingCampaigns
          }
          rows={
            data.getFundraisingAnalyticsDashboardData?.topPerformingCampaigns?.map(
              r => ({ ...r, id: r.campaignId })
            ) ?? []
          }
          columns={[
            {
              key: "campaignName",
              title: "Campaign Name",
              renderValue: row => row.campaignName,
              getSortableValue: row => row.campaignName,
            },
            {
              key: "campaignStartDate",
              title: "Campaign Start Date",
              renderValue: row => row.campaignStartDate,
              getSortableValue: row => row.campaignStartDate,
            },
            {
              key: "roi",
              title: "Overall ROI",
              renderValue: row => maybeNumberToPercentage(row.roi),
              getSortableValue: row => row.roi,
              getBarchartValue: row => row.roi,
            },
            {
              key: "textsSent",
              title: "Total Messages Sent",
              renderValue: row => row.textsSent.toLocaleString(),
              getSortableValue: row => row.textsSent,
            },
            {
              key: "totalRaised",
              title: "Total Amount Raised",
              renderValue: row => maybeNumberToDollars(row.totalRaisedDollars),
              getSortableValue: row => row.totalRaisedDollars,
            },
            {
              key: "donations",
              title: "Total Donations",
              renderValue: row => row.donations.toLocaleString(),
              getSortableValue: row => row.donations,
            },
            {
              key: "averageDonation",
              title: "Overall Avg Donation",
              renderValue: row =>
                maybeNumberToDollars(row.averageDonationDollars),
              getSortableValue: row => row.averageDonationDollars,
            },
            {
              key: "donorRate",
              title: "Donor Rate",
              renderValue: row =>
                maybeNumberToPercentage(row.donorConversionRate),
              getSortableValue: row => row.donorConversionRate,
            },
          ]}
          defaultSortColumnKey="roi"
          defaultSortDirection="desc"
          downloadableRows={data.getFundraisingAnalyticsDashboardData?.topPerformingCampaigns?.map(
            row => ({
              campaignId: row.campaignId,
              campaignName: row.campaignName,
              roi: row.roi.toFixed(4),
              textsSent: row.textsSent.toFixed(0),
              totalRaisedDollars: row.totalRaisedDollars.toFixed(2),
              donations: row.donations.toFixed(0),
              averageDonationDollars: row.averageDonationDollars.toFixed(2),
              donorConversionRate: row.donorConversionRate.toFixed(4),
            })
          )}
          downloadFileName="topPerformingCampaigns.csv"
        />
        <MonthlyRoisChart data={monthlyData} />
        <MonthlyDonationsByTypeChart data={monthlyData} />
        <MonthlyAverageDonationChart data={monthlyData} />
        <DonorConversionRateChart data={monthlyData} />
        <LinkConversionRateChart data={monthlyData} />
      </FlexBox>
    </>
  );
}

function FundraisingAnalyticsEmbed({
  subaccount,
}: {
  subaccount?: Subaccount;
}): ReactElement {
  const [timeframe, setTimeframe] = useState<Timeframe>(DEFAULT_TIMEFRAME);

  const query = subaccount
    ? ANALYTICS_EMBED_FOR_SUBACCOUNT
    : ANALYTICS_EMBED_FOR_TENANT;

  const subaccountId = subaccount?.id;

  const { loading, error, data, refetch } = useQuery(query, {
    variables: {
      subaccountId: subaccountId,
      timeframe,
      dashboardPath: "/embed/dashboards/6",
    },
    fetchPolicy: "network-only",
  });

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  const iframeUrl = subaccount
    ? data.analyticsEmbedForSubaccount.signedEmbedUrl
    : data.analyticsEmbedForTenant.signedEmbedUrl;

  const lookerSessionExpiresAt = subaccount
    ? data.analyticsEmbedForSubaccount.expiresAt
    : data.analyticsEmbedForTenant.expiresAt;

  return (
    <AnalyticsEmbed
      id="fundraising-embed"
      timeframe={timeframe}
      onChangeTimeframe={setTimeframe}
      iframeUrl={iframeUrl}
      height={2000}
      expiresAt={lookerSessionExpiresAt}
      refresh={refetch}
    />
  );
}
